import { Fragment } from 'react';
import { formatCurrency, formatNumber } from '../lib/utils';

/**
 * CorridaFinanciera — Transposed table: rows=partidas, columns=months
 * Section headers, group subheaders, and row labels are ALL sticky left
 * with opaque backgrounds so month data never overlaps them.
 */
export function CorridaFinanciera({ corrida }) {
  if (!corrida?.sections) return null;

  const months = corrida.months || 0;
  const labels = corrida.month_labels || [];

  const formatValue = (value, format) => {
    if (value === null || value === undefined) return '';
    if (value === 0 && format === 'currency') return '-';
    if (format === 'currency') return formatCurrency(value);
    if (format === 'pct') return `${formatNumber(value, 1)}%`;
    if (format === 'number') return formatNumber(value, 1);
    return value;
  };

  const sectionColors = {
    supuestos: { header: '#64748B', bg: '#F1F5F9' },
    ingresos:  { header: '#6366F1', bg: '#F5F3FF' },
    egresos:   { header: '#DC2626', bg: '#FEF2F2' },
    resumen:   { header: '#0F172A', bg: '#F1F5F9' },
  };

  // Solid white for card bg (no transparency issues)
  const CARD_BG = '#FFFFFF';

  return (
    <div className="overflow-auto border rounded-xl border-border/50" style={{ maxHeight: '600px', background: CARD_BG }}>
      <table className="text-xs" style={{ minWidth: `${200 + months * 100}px`, borderCollapse: 'collapse' }}>
        <thead className="sticky top-0" style={{ zIndex: 30 }}>
          <tr className="border-b-2 border-border">
            <th className="sticky left-0 text-left px-3 py-2.5 font-semibold text-sm min-w-[200px] border-r border-border"
                style={{ zIndex: 40, background: CARD_BG }}>
              PARTIDA
            </th>
            {labels.map((label, i) => (
              <th key={i} className="text-right px-2 py-2.5 font-medium text-muted-foreground min-w-[95px] whitespace-nowrap"
                  style={{ background: CARD_BG }}>
                {label}
              </th>
            ))}
            <th className="text-right px-3 py-2.5 font-semibold min-w-[110px] border-l-2 border-border"
                style={{ background: '#EDF1F7' }}>
              TOTAL
            </th>
          </tr>
        </thead>
        <tbody>
          {corrida.sections.map((section) => {
            const colors = sectionColors[section.id] || sectionColors.resumen;
            return (
              <Fragment key={section.id}>
                {/* Section header row */}
                <tr>
                  <td className="sticky left-0 px-3 py-2 font-bold text-white text-sm tracking-wide min-w-[200px]"
                      style={{ zIndex: 20, background: colors.header }}>
                    {section.title}
                  </td>
                  {labels.map((_, i) => (
                    <td key={i} style={{ background: colors.header }} className="py-2" />
                  ))}
                  <td style={{ background: colors.header }} className="py-2" />
                </tr>
                {section.groups.map((group, gi) => (
                  <Fragment key={gi}>
                    {/* Group subheader row */}
                    <tr>
                      <td className="sticky left-0 px-3 py-1.5 font-bold text-xs uppercase tracking-wide min-w-[200px]"
                          style={{ zIndex: 20, color: colors.header, background: colors.bg }}>
                        {group.title}
                      </td>
                      {labels.map((_, i) => (
                        <td key={i} style={{ background: colors.bg }} className="py-1.5" />
                      ))}
                      <td style={{ background: colors.bg }} className="py-1.5" />
                    </tr>
                    {/* Data rows */}
                    {group.rows.map((row) => {
                      const isTotal = row.is_total;
                      const nonNullValues = row.values.filter(v => v !== null && v !== undefined);
                      const total = nonNullValues.reduce((s, v) => s + v, 0);
                      const isCumulative = row.key.includes('acum') || row.key.includes('saldo') || row.key.includes('pct_individualizadas');
                      const isProjectOnly = row.key === 'res_utilidad' || row.key === 'res_pct_utilidad';
                      const hasOnlyOneValue = nonNullValues.length <= 1;
                      const displayTotal = isProjectOnly
                        ? (nonNullValues[nonNullValues.length - 1] || 0)
                        : hasOnlyOneValue ? nonNullValues[0] || 0
                        : isCumulative ? (nonNullValues[nonNullValues.length - 1] || 0)
                        : total;

                      const rowBg = isTotal ? colors.bg : CARD_BG;

                      return (
                        <tr
                          key={row.key}
                          className={`border-b border-border/30 hover:bg-accent/30 ${isTotal ? 'font-semibold' : ''}`}
                        >
                          <td className={`sticky left-0 px-3 py-1.5 border-r border-border whitespace-nowrap min-w-[200px] ${isTotal ? 'font-bold' : 'text-muted-foreground'}`}
                              style={{ zIndex: 20, color: isTotal ? colors.header : undefined, background: rowBg }}>
                            {isTotal ? row.label : `  ${row.label}`}
                          </td>
                          {row.values.map((val, mi) => {
                            const isNeg = val < 0;
                            return (
                              <td key={mi}
                                  className="text-right px-2 py-1.5 tabular-nums whitespace-nowrap"
                                  style={{ color: isNeg ? 'var(--danger)' : undefined, background: isTotal ? colors.bg : undefined }}>
                                {isProjectOnly ? '' : formatValue(val, row.format)}
                              </td>
                            );
                          })}
                          <td className="text-right px-3 py-1.5 tabular-nums whitespace-nowrap font-semibold border-l-2 border-border"
                              style={{ color: displayTotal < 0 ? 'var(--danger)' : undefined, background: '#EDF1F7' }}>
                            {formatValue(displayTotal, row.format)}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
